/**
 * Board 3 F · Cancel job (440) — `docs/job-stages.md` §5, §8 F: the job's
 * one cancel popup, from the job's ⋯ (or secondary), the quote's ⋯ and the
 * Jobs list's row menu — it replaces today's *Move to Cancelled* reason popup.
 *
 * Titled *Cancel <job ref>* with the job's name and status under it. **Why**
 * is required — **Cancel job** (danger) waits for it — and **What this does**
 * says each effect in one line, from `GET /jobs/:id/cancel-preview` (§14's
 * five): the sent quote expires, a draft stays read-only, the visits not
 * started are cancelled, and whether Reopen request can bring it back.
 * **Keep the job** is the way out. `POST /jobs/:id/cancel {reason}`.
 */
import { useEffect, useState } from 'react'
import type { CancelEffect, CancelPreviewAnswer } from '@rowted/shared'
import { humanize } from '../components/primitives.js'
import { cancelJob, readCancelPreview } from './api.js'
import { CANCEL_JOB, JOB_STATUS, quoteVersion, type EffectWords } from './copy.js'
import { FieldLabel, Fields, Refusal, StagePopup, TextBox, messageOf } from './popup.js'
import { hasWhy } from './rules.js'

export interface CancelJobProps {
  open: boolean
  jobId: string
  onClose: () => void
  /** After the server cancelled it: the job, and the *Why*. */
  onDone: (answer: { jobId: string; reason: string }) => void
  phone?: boolean
}

export function CancelJob(props: CancelJobProps) {
  if (!props.open) return null
  return <CancelJobBox {...props} />
}

/** One effect's words, in the board's weights. */
export function effectWords(effect: CancelEffect): EffectWords {
  switch (effect.kind) {
    case 'expires': return CANCEL_JOB.expires(quoteVersion(effect.quote.reference, effect.quote.version), effect.firstName)
    case 'draft_stays': return CANCEL_JOB.draftStays(quoteVersion(effect.quote.reference, effect.quote.version))
    case 'visits_cancelled': return CANCEL_JOB.visitsCancelled(effect.count)
    case 'can_reopen': return CANCEL_JOB.canReopen
    case 'cannot_reopen': return CANCEL_JOB.cannotReopen
  }
}

/** The job's status as its chip names it — Board 1's six, and today's chip word for a part-2 status. */
const statusWord = (status: string): string =>
  (JOB_STATUS as Record<string, string | undefined>)[status] ?? humanize(status)

function CancelJobBox({ jobId, onClose, onDone, phone }: CancelJobProps) {
  const [preview, setPreview] = useState<CancelPreviewAnswer | null>(null)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    readCancelPreview(jobId)
      .then((answer) => { if (live) setPreview(answer) })
      .catch((cause: unknown) => { if (live) setError(messageOf(cause)) })
    return () => { live = false }
  }, [jobId])

  async function cancel() {
    if (!hasWhy(why)) return
    setBusy(true)
    setError(null)
    try {
      await cancelJob(jobId, why.trim())
      onDone({ jobId, reason: why.trim() })
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <StagePopup
      width={440}
      title={preview ? CANCEL_JOB.title(preview.job.reference) : ''}
      {...(preview ? { sub: CANCEL_JOB.sub(preview.job.title, statusWord(preview.job.status)) } : {})}
      tone="danger"
      glyph="close"
      onClose={onClose}
      asDrawn="js-03 F"
      {...(phone === undefined ? {} : { phone })}
      buttons={[
        { label: CANCEL_JOB.keep, tone: 'plain', onClick: onClose },
        {
          label: CANCEL_JOB.cancel,
          tone: 'danger',
          disabled: busy || !hasWhy(why) || !preview?.cancellable,
          onClick: () => void cancel(),
        },
      ]}
    >
      <Fields>
        <div className="min-w-0">
          <FieldLabel htmlFor="cancel-why" label={CANCEL_JOB.why} required />
          <TextBox id="cancel-why" value={why} onChange={setWhy} autoFocus />
        </div>
        {preview && preview.effects.length > 0 && (
          <div className="rounded-card border border-line px-3 py-2.5 text-[12.5px] leading-[1.6] text-text-body">
            <b className="text-[13px] text-text">{CANCEL_JOB.what}</b>
            {preview.effects.map((effect, index) => {
              const words = effectWords(effect)
              return (
                <div key={`${effect.kind}-${index}`}>
                  {words.before}
                  {words.bold && <b>{words.bold}</b>}
                  {words.after}
                </div>
              )
            })}
          </div>
        )}
        <Refusal message={error} />
      </Fields>
    </StagePopup>
  )
}
